import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { api, ApiError } from '../../api/cliente.js';
import { Botao } from '../../componentes/ui/Botao.js';
import { Modal } from '../../componentes/ui/Modal.js';
import { LogProvisionamento } from './LogProvisionamento.js';

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  aoConcluir: () => void;
  /** IPs de malha já usados, para sugerir o próximo livre. */
  ipsMalhaEmUso: string[];
}

export function ModalAdicionarNo({ aberto, aoFechar, aoConcluir, ipsMalhaEmUso }: Props) {
  const [form, setForm] = useState({
    nome: '',
    hostname: '',
    ipPublico: '',
    ipMalha: proximoIpLivre(ipsMalhaEmUso),
    papel: 'DADOS' as 'DADOS' | 'ARBITRO',
    sshUsuario: 'root',
    sshPorta: 22,
    sshChavePrivada: '',
    sshEscalonamento: 'NENHUM' as 'NENHUM' | 'SUDO',
    sudoSenha: '',
  });
  const [jobId, setJobId] = useState<string | null>(null);
  const [concluido, setConcluido] = useState(false);

  const campo = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const testar = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; detalhe: string }>('/infra/nos/testar', {
        ipPublico: form.ipPublico,
        sshUsuario: form.sshUsuario,
        sshPorta: form.sshPorta,
        sshChavePrivada: form.sshChavePrivada,
        sshEscalonamento: form.sshEscalonamento,
        sudoSenha: form.sshEscalonamento === 'SUDO' ? form.sudoSenha || undefined : undefined,
      }),
  });

  const provisionar = useMutation({
    mutationFn: () =>
      api.post<{ jobId: string }>('/infra/nos', {
        ...form,
        // Sem sudo, a senha não vai no corpo — nem como string vazia, que o
        // backend cifraria como um segredo à toa.
        sudoSenha:
          form.sshEscalonamento === 'SUDO' && form.sudoSenha ? form.sudoSenha : undefined,
      }),
    onSuccess: (r) => setJobId(r.jobId),
  });

  function fechar() {
    // Só permite fechar durante a instalação se ela já terminou. Fechar no meio
    // não cancela nada — o job segue no servidor — mas o operador perderia o
    // log de vista sem saber disso.
    if (jobId && !concluido) return;
    setJobId(null);
    setConcluido(false);
    provisionar.reset();
    testar.reset();
    aoFechar();
  }

  const podeProvisionar =
    form.nome && form.hostname && form.ipPublico && form.ipMalha && form.sshChavePrivada;

  return (
    <Modal
      aberto={aberto}
      aoFechar={fechar}
      largura="largo"
      titulo={jobId ? 'Instalando o nó' : 'Adicionar nó ao cluster'}
      descricao={
        jobId
          ? 'A instalação continua no servidor mesmo se você fechar esta janela.'
          : 'O painel conecta por SSH e instala tudo do zero: WireGuard, Docker, MariaDB e PowerDNS.'
      }
      rodape={
        jobId ? (
          <Botao variante={concluido ? 'primario' : 'secundario'} onClick={fechar} disabled={!concluido}>
            {concluido ? 'Concluir' : 'Instalando…'}
          </Botao>
        ) : (
          <>
            <Botao onClick={fechar}>Cancelar</Botao>
            <Botao
              onClick={() => testar.mutate()}
              carregando={testar.isPending}
              disabled={!form.ipPublico || !form.sshChavePrivada}
            >
              Testar conexão
            </Botao>
            <Botao
              variante="primario"
              onClick={() => provisionar.mutate()}
              carregando={provisionar.isPending}
              disabled={!podeProvisionar}
            >
              Instalar
            </Botao>
          </>
        )
      }
    >
      {jobId ? (
        <LogProvisionamento
          jobId={jobId}
          aoTerminar={(status) => {
            setConcluido(true);
            if (status === 'OK') aoConcluir();
          }}
        />
      ) : (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              rotulo="Nome do nó"
              valor={form.nome}
              aoMudar={(v) => campo('nome', v.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              dica="minúsculas, números e hífen"
              placeholder="rdns3"
              mono
            />
            <Campo
              rotulo="Hostname público"
              valor={form.hostname}
              aoMudar={(v) => campo('hostname', v)}
              placeholder="rdns3.consultnetworks.com.br"
              mono
            />
            <Campo
              rotulo="IP público"
              valor={form.ipPublico}
              aoMudar={(v) => campo('ipPublico', v)}
              dica="usado para o SSH e para servir DNS"
              placeholder="200.150.30.10"
              mono
            />
            <Campo
              rotulo="IP na malha"
              valor={form.ipMalha}
              aoMudar={(v) => campo('ipMalha', v)}
              dica="endereço interno do WireGuard"
              mono
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <label className="block">
              <Rotulo>Papel</Rotulo>
              <select
                value={form.papel}
                onChange={(e) => campo('papel', e.target.value as 'DADOS' | 'ARBITRO')}
                className="h-11 w-full rounded-controle border border-borda px-3 text-[13.5px] focus:border-marca focus:outline-none"
              >
                <option value="DADOS">Dados — banco, DNS e painel</option>
                <option value="ARBITRO">Árbitro — só voto de quorum</option>
              </select>
            </label>
            <Campo
              rotulo="Usuário SSH"
              valor={form.sshUsuario}
              aoMudar={(v) => campo('sshUsuario', v)}
              mono
            />
            <Campo
              rotulo="Porta SSH"
              valor={String(form.sshPorta)}
              aoMudar={(v) => campo('sshPorta', Number(v) || 22)}
              mono
            />
          </div>

          <label className="block">
            <Rotulo>Chave privada SSH</Rotulo>
            <textarea
              value={form.sshChavePrivada}
              onChange={(e) => campo('sshChavePrivada', e.target.value)}
              rows={4}
              spellCheck={false}
              placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
              className="w-full rounded-controle border border-borda p-3 font-mono text-[12px] focus:border-marca focus:outline-none"
            />
            <span className="mt-1.5 block text-[12px] text-texto-suave">
              Guardada cifrada no banco. É a chave do host que está entrando, não a chave de
              deploy do cluster.
            </span>
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <Rotulo>Como virar root</Rotulo>
              <select
                value={form.sshEscalonamento}
                onChange={(e) => campo('sshEscalonamento', e.target.value as 'NENHUM' | 'SUDO')}
                className="h-11 w-full rounded-controle border border-borda px-3 text-[13.5px] focus:border-marca focus:outline-none"
              >
                <option value="NENHUM">O usuário já é root</option>
                <option value="SUDO">Escalar com sudo</option>
              </select>
              <span className="mt-1.5 block text-[12px] text-texto-suave">
                Imagens de nuvem entram como <code className="mono">ubuntu</code> e usam{' '}
                <code className="mono">sudo</code>.
              </span>
            </label>

            {form.sshEscalonamento === 'SUDO' && (
              <label className="block">
                <Rotulo>Senha do sudo</Rotulo>
                <input
                  type="password"
                  value={form.sudoSenha}
                  autoComplete="new-password"
                  placeholder="vazio se o sudo não pede senha"
                  onChange={(e) => campo('sudoSenha', e.target.value)}
                  className="h-11 w-full rounded-controle border border-borda px-3 font-mono text-[13.5px] focus:border-marca focus:outline-none"
                />
                <span className="mt-1.5 block text-[12px] text-texto-suave">
                  Guardada cifrada. A maioria das VMs de nuvem dá sudo sem senha.
                </span>
              </label>
            )}
          </div>

          {testar.data && (
            <p
              className={`flex items-start gap-2 rounded-chip px-3 py-2 text-[12.5px] font-semibold ${
                testar.data.ok ? 'bg-sucesso-fundo text-sucesso' : 'bg-erro-fundo text-erro'
              }`}
            >
              {testar.data.ok ? (
                <CheckCircle2 size={15} className="mt-px shrink-0" />
              ) : (
                <AlertTriangle size={15} className="mt-px shrink-0" />
              )}
              <span className="whitespace-pre-wrap">{testar.data.detalhe}</span>
            </p>
          )}

          {(testar.error || provisionar.error) && (
            <p className="rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
              {((testar.error ?? provisionar.error) as ApiError).mensagem}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

function Rotulo({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
      {children}
    </span>
  );
}

function Campo({
  rotulo,
  valor,
  aoMudar,
  dica,
  placeholder,
  mono,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (v: string) => void;
  dica?: string;
  placeholder?: string;
  mono?: boolean;
}) {
  return (
    <label className="block">
      <Rotulo>{rotulo}</Rotulo>
      <input
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        placeholder={placeholder}
        spellCheck={false}
        className={`h-11 w-full rounded-controle border border-borda px-3 text-[13.5px] focus:border-marca focus:outline-none ${
          mono ? 'font-mono' : ''
        }`}
      />
      {dica && <span className="mt-1 block text-[11.5px] text-texto-suave">{dica}</span>}
    </label>
  );
}

/** Sugere o próximo endereço livre da malha, para o operador não ter que conferir. */
function proximoIpLivre(emUso: string[]): string {
  const usados = new Set(emUso);
  for (let i = 1; i < 250; i++) {
    const ip = `10.77.0.${i}`;
    if (!usados.has(ip)) return ip;
  }
  return '';
}
